import React, { useState } from 'react';
import type { ApiCountry } from './AddressFields';

interface PhoneInputFieldProps {
  value: string;
  onChange: (value: string) => void;
  phoneCode?: string;                       // controlled — caller owns the selected code
  onPhoneCodeChange?: (code: string) => void;
  countries?: ApiCountry[];                 // list for the code dropdown
  error?: string;
  variant?: 'dark' | 'light';
}

function validatePhone(v: string): string {
  const digits = v.replace(/\D/g, '');
  if (digits.length < 7)  return 'Too short — enter at least 7 digits';
  if (digits.length > 15) return 'Too long — max 15 digits';
  return '';
}

// Parse a stored E.164 number back into code + local parts.
// Sorts by longest code first so "+44..." isn't matched by "+1".
export function parseStoredPhone(phone: string, countries: ApiCountry[]): { code: string; local: string } {
  if (!phone || !phone.startsWith('+')) return { code: '', local: phone };
  const sorted = [...countries].sort((a, b) => b.phone_code.length - a.phone_code.length);
  for (const c of sorted) {
    if (phone.startsWith(c.phone_code)) {
      return { code: c.phone_code, local: phone.slice(c.phone_code.length) };
    }
  }
  return { code: '', local: phone };
}

export function buildE164(localNumber: string, phoneCode: string): string {
  const trimmed = localNumber.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) return trimmed;
  if (!phoneCode) return trimmed;
  // Strip trunk prefix 0 (UK, AU, etc.) before prepending country code
  const national = trimmed.startsWith('0') ? trimmed.slice(1) : trimmed;
  return `${phoneCode}${national}`;
}

export default function PhoneInputField({
  value, onChange, phoneCode, onPhoneCodeChange, countries = [], error: externalError, variant = 'dark',
}: PhoneInputFieldProps) {
  const [blurError, setBlurError] = useState('');
  const displayError = externalError || blurError;

  const selectCls = variant === 'light'
    ? `text-sm border border-r-0 rounded-l-lg px-2 py-2 bg-gray-50 text-gray-600 focus:outline-none focus:ring-1 focus:ring-primary-500 ${displayError ? 'border-red-400' : 'border-gray-200'}`
    : `text-sm border border-r-0 rounded-l-lg px-2 py-2 bg-dark-hover text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 ${displayError ? 'border-red-500' : 'border-dark-border'}`;

  const badgeCls = variant === 'light'
    ? `px-3 py-2 text-sm border border-r-0 rounded-l-lg bg-gray-50 text-gray-500 select-none ${displayError ? 'border-red-400' : 'border-gray-200'}`
    : `px-3 py-2 text-sm border border-r-0 rounded-l-lg bg-dark-hover text-gray-400 select-none ${displayError ? 'border-red-500' : 'border-dark-border'}`;

  const inputCls = variant === 'light'
    ? `flex-1 text-sm border rounded-r-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${displayError ? 'border-red-400 bg-red-50' : 'border-gray-200'}`
    : `flex-1 text-sm bg-dark-canvas border rounded-r-lg px-3 py-2 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${displayError ? 'border-red-500' : 'border-dark-border'}`;

  return (
    <div>
      <div className="flex">
        {countries.length > 0 ? (
          <select
            value={phoneCode}
            onChange={e => onPhoneCodeChange?.(e.target.value)}
            className={selectCls}
          >
            {countries.map(c => (
              <option key={c.iso_code} value={c.phone_code}>
                {c.iso_code} {c.phone_code}
              </option>
            ))}
          </select>
        ) : phoneCode ? (
          <span className={badgeCls}>{phoneCode}</span>
        ) : null}
        <input
          type="tel"
          value={value}
          placeholder="Phone number"
          onChange={e => { onChange(e.target.value); setBlurError(''); }}
          onBlur={() => { if (value) setBlurError(validatePhone(value)); }}
          className={!phoneCode && countries.length === 0
            ? inputCls.replace('rounded-r-lg', 'rounded-lg')
            : inputCls}
        />
      </div>
      {displayError && <p className="text-red-500 text-xs mt-1">{displayError}</p>}
    </div>
  );
}
